<template>
    <v-layout v-if="getFirstAppointment" row wrap id="body-wrapper">
        <v-flex xs12 md8 class="pt-4" :class="{'pl-5': $vuetify.breakpoint.mdAndUp, 'pl-3 pb-6': !$vuetify.breakpoint.mdAndUp}">
            <h1
                class="headline primary--text py-4 font-weight-bold"
                :aria-label="$t('coupon.thanksAriaLabel', [getApptsNumber])"
                aria-hidden="false"
                tabindex="0"
                id="skip-to-main-content-start">
                {{ $t('coupon.thanks') }}
            </h1>

            <span tabindex="0">
                <coupon-service :service="appointmentService" />
                <coupon-appt-number :appointment="getFirstAppointment" :appts-number="getApptsNumber" />
                <coupon-total-appt-booked :appts-number="getApptsNumber" />
                <coupon-appt-date :appointment="getFirstAppointment" />
                <coupon-appt-hour :appointment="getFirstAppointment" :service="appointmentService" :is-virtual-service="isVirtualService" />
                <coupon-place v-if="!isHidingAppointmentLocation" :place="appointmentPlace" />
                <coupon-address v-if="!isHidingAppointmentLocation" :place="appointmentPlace" />
                <coupon-pricing v-if="!isFluTarificationModeActivated" :service="appointmentService" />
                <coupon-scanned-request :appointment="getFirstAppointment " />
                <coupon-print-button @print="print" />
            </span>
        </v-flex>

        <coupon-image />
        <coupon-create-profile
            v-if="shouldDisplayCreateProfileButton"
            :client-data="getFirstAppointment.clientData"
        />
        <coupon-co-administration-reminder :service="appointmentService" :appointment="getFirstAppointment" />
        <span tabindex="0">
            <coupon-place-details :place="appointmentPlace" />
            <coupon-service-details :service="appointmentService" />
            <coupon-communication-details :appointment="getFirstAppointment" :is-virtual-service="isVirtualService" />
        </span>
        <coupon-book-another-appointment />
    </v-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import printJS from 'print-js';
import CouponCommunicationDetails from '@/components/coupon/CouponCommunicationDetails';
import CouponApptNumber from '@/components/coupon/CouponApptNumber';
import CouponService from '@/components/coupon/CouponService';
import CouponTotalApptBooked from '@/components/coupon/CouponTotalApptBooked';
import CouponApptDate from '@/components/coupon/CouponApptDate';
import CouponApptHour from '@/components/coupon/CouponApptHour';
import CouponPlace from '@/components/coupon/CouponPlace';
import CouponAddress from '@/components/coupon/CouponAddress';
import CouponPricing from '@/components/coupon/CouponPricing';
import CouponScannedRequest from '@/components/coupon/CouponScannedRequest';
import CouponServiceDetails from '@/components/coupon/CouponServiceDetails';
import CouponPlaceDetails from '@/components/coupon/CouponPlaceDetails';
import CouponPrintButton from '@/components/coupon/CouponPrintButton';
import CouponImage from '@/components/coupon/CouponImage';
import CouponBookAnotherAppointment from '@/components/coupon/CouponBookAnotherAppointment';
import { find, get } from 'lodash';
import { ServiceSettings } from '@/enums';
import CouponCreateProfile from '@/components/coupon/CouponCreateProfile/CouponCreateProfile';
import CouponCoAdministrationReminder from '@/components/coupon/coupon-co-administration-reminder/CouponCoAdministrationReminder';
import { authModule } from '@/utils';
export default {
    name: 'coupon',
    components: {
        CouponCreateProfile,
        CouponCommunicationDetails,
        CouponAddress,
        CouponApptDate,
        CouponApptHour,
        CouponApptNumber,
        CouponImage,
        CouponPlace,
        CouponPlaceDetails,
        CouponPricing,
        CouponPrintButton,
        CouponScannedRequest,
        CouponService,
        CouponServiceDetails,
        CouponTotalApptBooked,
        CouponBookAnotherAppointment,
        CouponCoAdministrationReminder,
    },

    async created() {
        if (this.getFirstAppointment === null) {
            this.toggleHomeLoading({ set: true });
            await this.$router.push('take-appt');
        }
        else {
            await this.fetchEstConfigurationMessages(this.getFirstAppointment.establishmentServiceTemplate);
            this.toggleHomeLoading({ set: false });
        }
    },

    computed: {
        ...mapGetters({
            isClicSante: 'isClicSante',
            upperLocale: 'upperLocale',
            locale: 'locale',
            getApptsNumber: 'appointments/getApptsNumber',
            getFirstAppointment: 'appointments/getFirstAppointment',
            getService: 'services/getInstantiatedService',
            getServiceByID: 'services/getServiceByID',
            getPlace: 'places/getPlace',
            getCurrentEstSettings: 'services/getCurrentEstSettings',
            getLoggedUser: `${authModule}/getLoggedUser`,
        }),

        shouldDisplayCreateProfileButton() {
            return this.isClicSante && !this.getLoggedUser;
        },

        isFluTarificationModeActivated() {
            const setting = find(this.getCurrentEstSettings, {
                setting: ServiceSettings.FLU_TARIFICATION_MODE_VALUE,
            });
            return setting && setting.value === 'flu';
        },

        /**
         * Returns appointment's service
         */
        appointmentService() {
            return this.getFirstAppointment
                ? this.getService(this.getFirstAppointment.establishmentServiceTemplate)
                : {};
        },

        /**
         * Returns appointment's place
         */
        appointmentPlace() {
            return this.getFirstAppointment
                ? this.getPlace(this.getFirstAppointment.place)
                : null;
        },

        /**
         * Returns true if in the client data there is a virtual account associated
         */
        isVirtualService() {
            const setting = find(this.getCurrentEstSettings, {
                setting: ServiceSettings.IS_VIRTUAL_SERVICE,
            });
            return setting && setting.value;
        },

        isHidingAppointmentLocation() {
            return get(this.getFirstAppointment, 'isDistanceHidden') ||
                !get(this.getPlace(this.getFirstAppointment.place), 'isPhysicalLocation');
        },
    },
    methods: {
        ...mapActions({
            toggleHomeLoading: 'loading/toggleHomeLoading',
            fetchEstConfigurationMessages: 'services/fetchEstConfigurationMessages',
        }),

        print() {
            setTimeout(() => {
                const title = this.upperLocale === 'Fr'
                    ? 'Coupon de confirmation'
                    : 'Appointment Ticket';
                printJS({
                    printable: 'body-wrapper',
                    type: 'html',
                    ignoreElements: [
                        'coupon-print-btn',
                        'coupon-img',
                        'coupon-pdf-btn',
                        'coupon-back-btn',
                        'coupon-sendSms-btn',
                        'coupon-another-btn',
                        'coupon-create-profile',
                    ],
                    documentTitle: title,
                    scanStyles: false,
                    css: '/css/coupon-print.css',
                }, 200);
            });
        },
    },
};
</script>

<style lang="scss">

</style>
